/**
 * SocialPulseHeader — clean editorial nav for the new socialpulse.com home.
 *
 * Sticky on scroll; opaque with a subtle bottom hairline. No emoji, no
 * gradient bar. Search + language + Sign in + primary "Join SocialPulse"
 * CTA. The search icon opens an inline slide-down bar that submits to
 * `/search?q=…` and works on both desktop and mobile.
 */
import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ChevronDown, Menu, X, Activity } from "lucide-react";

const NAV = [
  { to: "/explore",     label: "Explore" },
  { to: "/places",      label: "Places" },
  { to: "/topics",      label: "Topics" },
  { to: "/community",   label: "Community" },
  { to: "/causes",      label: "Causes" },
  { to: "/network",     label: "Network" },
  { to: "/about",       label: "About" },
];

const LANGS = [
  ["en", "English"], ["hi", "हिन्दी"], ["ml", "മലയാളം"],
  ["ta", "தமிழ்"], ["te", "తెలుగు"], ["kn", "ಕನ್ನಡ"],
  ["mr", "मराठी"], ["bn", "বাংলা"], ["gu", "ગુજરાતી"],
  ["pa", "ਪੰਜਾਬੀ"], ["fr", "Français"], ["es", "Español"],
  ["ar", "العربية"],
];

export default function SocialPulseHeader() {
  const nav = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [mobOpen, setMobOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const langRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      // give the transition a tick so autoFocus lands after mount
      setTimeout(() => searchInputRef.current?.focus(), 60);
    }
  }, [searchOpen]);

  function submitSearch(e) {
    e.preventDefault();
    const query = q.trim();
    if (query.length < 2) return;
    setSearchOpen(false);
    setMobOpen(false);
    nav(`/search?q=${encodeURIComponent(query)}`);
  }

  return (
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200"
      data-testid="sp-header"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-2 sm:gap-4">
        {/* Wordmark */}
        <Link to="/" data-testid="sp-wordmark" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-violet-600 text-white shadow-sm">
            <Activity className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <span className="font-serif font-bold text-slate-900 text-lg sm:text-xl tracking-tight">
            SocialPulse<span className="text-slate-400 text-sm align-top">®</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 ml-6 text-[15px] font-medium text-slate-700">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`sp-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `transition-colors hover:text-blue-700 ${isActive ? "text-blue-700" : ""}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <button
            aria-label="Search"
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen((o) => !o)}
            data-testid="sp-search-btn"
            className={`w-9 h-9 grid place-items-center rounded-full text-slate-700 transition-colors ${
              searchOpen ? "bg-blue-50 text-blue-700" : "hover:bg-slate-100"
            }`}
          >
            <Search className="w-4 h-4" />
          </button>
          <div className="hidden sm:block w-px h-6 bg-slate-200" />

          {/* Language */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen((o) => !o)}
              data-testid="sp-lang-btn"
              className="hidden sm:inline-flex items-center gap-1 h-9 px-2.5 rounded-full hover:bg-slate-100 text-slate-700 text-sm"
            >
              {LANGS.find(([c]) => c === lang)?.[1] || "English"}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {langOpen && (
              <div
                data-testid="sp-lang-menu"
                className="absolute right-0 mt-2 w-52 max-h-[70vh] overflow-y-auto rounded-2xl bg-white ring-1 ring-slate-200 shadow-xl py-2 z-50"
              >
                {LANGS.map(([code, label]) => (
                  <button
                    key={code}
                    onClick={() => { setLang(code); setLangOpen(false); }}
                    data-testid={`sp-lang-option-${code}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left ${
                      code === lang ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="font-mono text-[10px] w-6 uppercase text-slate-400">{code}</span>
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/login"
            data-testid="sp-signin-btn"
            className="hidden sm:inline-flex items-center h-9 px-3 rounded-full text-slate-700 hover:bg-slate-100 text-sm font-semibold"
          >
            Sign In
          </Link>
          <Link
            to="/join"
            data-testid="sp-join-btn"
            className="inline-flex items-center h-9 px-3 sm:px-4 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-semibold shadow-sm transition-colors whitespace-nowrap"
          >
            <span className="hidden sm:inline">Join SocialPulse</span>
            <span className="sm:hidden">Join</span>
          </Link>

          <button
            aria-label="Menu"
            data-testid="sp-mobnav-btn"
            onClick={() => setMobOpen((o) => !o)}
            className="lg:hidden w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100 text-slate-700 ml-0.5"
          >
            {mobOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Inline search bar */}
      {searchOpen && (
        <div className="border-t border-slate-200 bg-white" data-testid="sp-search-bar">
          <form onSubmit={submitSearch} className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-3">
            <label className="relative block">
              <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search places, people, topics, civic issues, events, stories…"
                data-testid="sp-header-search-input"
                className="w-full h-11 pl-11 pr-24 rounded-full ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm text-slate-800 placeholder:text-slate-400 bg-slate-50 focus:bg-white transition-colors"
              />
              <button
                type="submit"
                data-testid="sp-header-search-submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center h-8 px-4 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
              >
                Search
              </button>
            </label>
          </form>
        </div>
      )}

      {mobOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="sp-mobnav">
          <nav className="max-w-[1400px] mx-auto px-6 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMobOpen(false)}
                className="py-2 text-slate-700 hover:text-blue-700 font-medium"
              >
                {n.label}
              </Link>
            ))}
            <div className="mt-2 pt-3 border-t border-slate-100 flex items-center gap-2">
              <Link
                to="/login"
                onClick={() => setMobOpen(false)}
                data-testid="sp-mobnav-signin"
                className="flex-1 text-center h-10 leading-10 rounded-full ring-1 ring-slate-300 text-slate-700 hover:text-blue-700 hover:ring-blue-300 text-sm font-semibold"
              >
                Sign In
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
